import { Box, Switch, FormControlLabel, useMediaQuery } from '@mui/material'
import { Table } from 'components/core-ui/Table'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { useNotification } from 'hooks'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { makeCoreGetRequest } from 'utils/api'
import { formatErrorMessage } from 'utils/formatting'
import { CancelProformaModal } from './CancelProformaModal'
import s from './ProformaTable.modules.scss'
import { defaultTableFilters, tableColumns } from './constants'
import {
  ProformaListResponseModel,
  ProformaTableProps,
  TableFilters,
  TableRowModel
} from './interfaces'
import { toTableRowItems } from './serializer'
import { buildTableFilterInputs } from './utils'
import { CancelActionIcon, InvoiceActionIcon } from 'icons'

const ProformaTable: React.FC<ProformaTableProps> = props => {
  const { proformaType } = props

  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const isXs = useMediaQuery('(max-width:624px)')
  const [tableItems, setTableItems] = useState<TableRowModel[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [selectedRow, setSelectedRow] = useState<TableRowModel>()
  const [tableFilters, setTableFilters] = useState<TableFilters>(defaultTableFilters)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)

  const [withMyProforma, setWithMyProforma] = useState(true)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false)

  const columnsWithActions = useMemo(() => tableColumns, [])
  const tableFilterInputs = useMemo(() => buildTableFilterInputs(tableFilters, setTableFilters), [])

  const openCancelModal = (): void => setIsCancelModalOpen(true)
  const closeCancelModal = (): void => setIsCancelModalOpen(false)

  const navigateToPrint = (id: number, clientId: string | number) => {
    navigate(`/supplyOmni/proformaView/${id}?clientId=${clientId}`)
  }

  const handleWithMyProformaFilterChange = (value): void => {
    setWithMyProforma(value)
    setShouldRefreshData(true)
    setShouldResetPage(true)
  }

  const rowActions = useMemo(() => {
    const selectRow = (rowId: number, action: string) => {
      const item = tableItems.find(item => item.id === rowId)
      setSelectedRow(item)
      action === 'print' ? navigateToPrint(item.id, item.clientId) : openCancelModal()
    }

    const actions = [
      proformaType === 'active'
        ? {
            label: 'Anulare',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#CC4010',
            desktopIcon: <CancelActionIcon />,
            onClick: (e, rowId) => selectRow(rowId, 'delete')
          }
        : null,
      {
        label: 'Detalii',
        labelColor: 'white',
        typographyVariant: 'medium',
        backgroundColor: '#0970E6',
        desktopIcon: <InvoiceActionIcon />,
        onClick: (e, rowId) => selectRow(rowId, 'print')
      }
    ].filter(Boolean)

    return actions
  }, [tableColumns, tableItems])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = (await makeCoreGetRequest(
        toTableGETUrl({
          url: `/supply-omni/order${proformaType === 'active' ? '/active' : ''}`,
          tableFilters,
          extraCustomUrlParams: [{ name: 'myProformas', value: String(withMyProforma) }],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )) as ProformaListResponseModel
      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      showNotification({
        message: `Error while fetching table data. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }
  return (
    <div className={s.mainContainer}>
      <Box>
        <CancelProformaModal
          isModalOpen={isCancelModalOpen}
          closeModal={closeCancelModal}
          orderId={`${selectedRow?.id}`}
          setShouldRefreshData={setShouldRefreshData}
        />
        <FormControlLabel
          sx={isXs ? {} : { position: 'absolute', width: '50%', zIndex: 3, color: '#052351' }}
          name="my_bills1"
          label="Proformele mele"
          control={
            <Switch
              checked={withMyProforma}
              onChange={event => handleWithMyProformaFilterChange(event.target.checked)}
            />
          }
        />
        <Table
          hideCheckbox
          order="desc"
          orderBy="creationDate"
          columns={columnsWithActions}
          showCounter={isXs}
          counterLabel={isXs ? 'Total facturi proforme' : undefined}
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          rowActions={rowActions}
          rowActionsWidth="200px"
          filters={tableFilterInputs}
          filtersLabel={"Filtre"}
          appliedFiltersLabel={"Filtre aplicate"}
          filtersButtonLabel={"Filtrare"}
          cancelFiltersButtonLabel={"Anulare"}
          applyFiltersButtonLabel={"Confirma"}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          shouldResetPage={shouldResetPage}
          setShouldResetPage={setShouldResetPage}
        />
      </Box>
    </div>
  )
}

export default ProformaTable
